import alternativeCountryImage from "@/_assets/images/countryImg.png";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { usePathname } from "@/i18n/navigation";
import { cn } from "@/lib/utils";
import { ChevronRight } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import CountryHeadingWithFlag from "./CountryHeadingWithFlag";
import Image from "next/image";

function CountryImageAndHeadings({
  countryImage,
  countryName,
  countryFlag,
}: {
  countryImage: string | null;
  countryName: string;
  countryFlag: string;
}) {
  const t = useTranslations();
  const pathName = usePathname();

  const isDataVoice = pathName.endsWith("esim/");
  const isGlobalPage =
    pathName.startsWith("/international-esim") ||
    pathName.startsWith("/global");
  const [isOpen, setIsOpen] = useState(false);
  const [isMount, setIsMount] = useState<true | undefined>(true);

  useEffect(function () {
    setIsMount(undefined);
  }, []);

  const myCountryImage = countryImage
    ? typeof countryImage == "string" && !countryImage.startsWith("http")
      ? alternativeCountryImage
      : countryImage
    : alternativeCountryImage;
  return (
    <div>
      <div className={`relative w-full rounded-[2.5rem] xl:flex xl:h-[720px]`}>
        <Image
          src={myCountryImage}
          fill
          sizes="auto"
          alt={`${countryName} image`}
          priority
          quality={70}
          className="hidden rounded-[2.5rem] object-cover xl:block"
        />

        <div className="absolute inset-0 hidden rounded-[2.5rem] bg-gradient-to-b from-black/80 via-black/40 to-transparent xl:block"></div>
        <div className="z-20 flex flex-col items-center justify-center gap-4 md:gap-6 xl:absolute xl:top-2 xl:text-background xl:opacity-95">
          <div className="flex flex-col gap-3 rounded-[2.5rem] px-4 pb-0 pt-4 xl:gap-4 xl:rounded-b-none xl:rounded-t-[2.5rem] xl:p-4">
            {!isGlobalPage ? (
              <h1 className="flex flex-col items-center justify-center gap-3 xl:gap-6">
                {isDataVoice ? (
                  <>
                    <CountryHeadingWithFlag
                      title={t(
                        "DataVoiceLocalPage.countryPackages.topText.withSvg",
                      )}
                      countryFlag={countryFlag}
                      countryName={countryName}
                      description={t(
                        "DataVoiceLocalPage.countryPackages.topText.withFlag",
                        { countryName },
                      )}
                    />
                  </>
                ) : (
                  <>
                    <CountryHeadingWithFlag
                      title={t(
                        "DataOnlyLocalPage.countryPackages.topText.withSvg",
                      )}
                      countryFlag={countryFlag}
                      countryName={countryName}
                      description={t(
                        "DataOnlyLocalPage.countryPackages.topText.withFlag",
                        { countryName },
                      )}
                    />
                  </>
                )}
              </h1>
            ) : (
              <p className="flex flex-col items-center justify-center gap-3 xl:gap-6">
                <CountryHeadingWithFlag
                  title={t(
                    "DataOnlyGlobalPage.countryPackages.topText.withSvg",
                  )}
                  countryFlag={countryFlag}
                  countryName={countryName}
                  description={t(
                    "DataOnlyGlobalPage.countryPackages.topText.withFlag",
                    { countryName },
                  )}
                />
              </p>
            )}

            {/* Desktop / large screens paragraph (keeps current overlay behavior) */}
            <p className="hidden text-center text-body-sm text-foreground dark:text-primary-foreground md:text-body-base xl:flex xl:text-primary-foreground">
              {isDataVoice
                ? t(
                    "DataVoiceLocalPage.countryPackages.topText.withHeroImage",
                    { countryName },
                  )
                : t("DataOnlyLocalPage.countryPackages.topText.withHeroImage", {
                    countryName,
                  })}
            </p>
          </div>
        </div>
      </div>
      <div className="mb-3 w-full xl:hidden">
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
          <CollapsibleTrigger className="flex w-full items-center justify-center px-4 py-0.5 text-sm font-medium text-foreground dark:text-primary-foreground">
            <span className="flex items-center gap-2">
              <ChevronRight
                className={cn(
                  "h-6 w-6 text-foreground transition-transform duration-200 dark:text-primary-foreground",
                  isOpen && "rotate-90",
                )}
              />
              <span>
                {isOpen
                  ? t("Shared.countryPackages.seeDetailToggle.closeDetails")
                  : t("Shared.countryPackages.seeDetailToggle.seeDetails")}
              </span>
            </span>
          </CollapsibleTrigger>
          <CollapsibleContent
            forceMount={isMount}
            className="CollapsibleContent my-1 text-center text-body-sm text-foreground dark:text-primary-foreground md:px-[6rem]"
          >
            {isDataVoice
              ? t("DataVoiceLocalPage.countryPackages.topText.withHeroImage", {
                  countryName,
                })
              : t("DataOnlyLocalPage.countryPackages.topText.withHeroImage", {
                  countryName,
                })}

            <h2 className="mt-2 text-center font-montserrat text-lg font-600 md:text-[1.25rem] xl:hidden xl:text-start">
              {t("Shared.countryPackages.withPackages", { countryName })}
            </h2>
          </CollapsibleContent>
        </Collapsible>
      </div>
    </div>
  );
}

export default CountryImageAndHeadings;
